import {
    ApiUserConversationsForPost,
    PollOption,
    PostResult,
    RequestCriteriaType,
} from "./PostService.types";
import * as client from "../ClientConsumer";
import { prepareSearchCriteria } from "./ApiUtility";
import { generatePath } from "react-router-dom";
import {
    PollType,
    PostCreationResponse,
    PostIdType,
    PostsCollectionResult,
} from "../../Presentation/Components/Post/Post.types";
import { POI_PREFIX } from "../../Presentation/Components/Group/PointsOfInterest/PointsOfInterest.service";
import { PollResponse } from "@infrastructure/Services/AuthService";
import { PollTypesEnum } from "../../Presentation/Shared/Types/Polls";
import { clientWithoutCache } from "@infrastructure/BackendClient";
import { isPlatformTrucktour } from "./EnvService";

const routes = {
    GET_POST: isPlatformTrucktour ? "/posts/:id/details" : "/posts/:id",
    GET_POST_BY_GAME: "/games/:gameId/post",
    DELETE_POST: "/posts/:postId",
    POST_REACTION_TO_POST: "/posts/:postId/reaction",
    DELETE_REACTION_FROM_POST: "/posts/:postId/reaction/:reactionTypeName",
    POST_POLL_REPSPONSE: "/posts/:postId/poll-add-response/:pollOptionId",
    DELETE_POLL_RESPONSE: "/posts/:postId/poll-remove-response",
    GET_LATEST_REACTIONS_ON_POST: "/posts/:postId/latest-reactions",
    GET_LOCATION_POSTS: "/community/locations/:id/posts",
    GET_ADJACENT_POSTS: "/community/posts/:id/adjacents",
    GET_USER_CONVERSATIONS_FOR_POST: "/chat/conversations-for-post",
};

export function addPollResponse(
    postId: PostIdType,
    pollOptionId: number,
): Promise<PollResponse> {
    return client
        .postData(
            generatePath(routes.POST_POLL_REPSPONSE, {
                postId: postId,
                pollOptionId: pollOptionId,
            }),
            {},
        )
        .then((response) => response.data);
}

export function removePollResponse(postId: PostIdType) {
    return client
        .deleteData(
            generatePath(routes.DELETE_POLL_RESPONSE, {
                postId: postId,
            }),
        )
        .then((response) => response.data);
}

export async function getPost(
    id: string,
    GetPostRequestCriteria?: RequestCriteriaType,
    noCache: boolean = false,
): Promise<PostResult> {
    const url = `${routes.GET_POST.replace(":id", id)}${prepareSearchCriteria(
        GetPostRequestCriteria,
    )}`;
    const promise = noCache
        ? clientWithoutCache.get<{ post: PostResult }>(url)
        : client.getData(url);
    return promise.then((res) => res.data.post);
}

export async function getPostByGame(gameId: string): Promise<PostResult> {
    return client
        .getData(
            `${routes.GET_POST_BY_GAME}${prepareSearchCriteria({})}`.replace(
                ":gameId",
                gameId,
            ),
        )
        .then((res) => res.data.post);
}

export function addReactionToPost(
    postId: number | string,
    reactionTypeName: string,
) {
    return client
        .postData(
            generatePath(routes.POST_REACTION_TO_POST, {
                postId: postId,
            }),
            {
                reactionTypeName: reactionTypeName,
            },
        )
        .then((response) => response.data);
}

export function removeReactionFromPost(
    postId: number | string,
    reactionTypeName: string,
) {
    return client
        .deleteData(
            generatePath(routes.DELETE_REACTION_FROM_POST, {
                postId: postId,
                reactionTypeName: reactionTypeName,
            }),
        )
        .then((response) => response.data);
}

export function getLatestReactionsOnPost(postId: string) {
    return client
        .getData(
            generatePath(routes.GET_LATEST_REACTIONS_ON_POST, {
                postId: postId,
            }) + prepareSearchCriteria({}),
        )
        .then((response) => response.data);
}

export function createLocationPost(
    id: string,
    data: any,
): Promise<PostCreationResponse> {
    return client
        .postData(routes.GET_LOCATION_POSTS.replace(":id", id), data)
        .then((res) => {
            return res.data;
        });
}

export function getLocationPosts(
    id: string,
    sortCriteria?: RequestCriteriaType,
): Promise<PostsCollectionResult> {
    return client
        .getData(
            `${routes.GET_LOCATION_POSTS.replace(
                ":id",
                id,
            )}${prepareSearchCriteria(sortCriteria)}`,
        )
        .then((res) => res.data);
}

export function extractLocationId(id: string) {
    return id.replace(POI_PREFIX, "");
}

export function getUserConversationsForPost(
    userUuid?: string,
): Promise<Array<ApiUserConversationsForPost>> {
    return client
        .getData(
            routes.GET_USER_CONVERSATIONS_FOR_POST +
                prepareSearchCriteria({ userId: userUuid }),
        )
        .then((res) => {
            return res.data.result;
        });
}

export function deletePost(postId: number) {
    return client
        .deleteData(routes.DELETE_POST.replace(":postId", postId.toString()))
        .then((res) => res.data.result);
}

export function mapPoll(
    pollData: PollType | undefined,
): (PollType & { pollOptions: PollOption[] }) | undefined {
    if (!pollData) {
        return undefined;
    }

    return {
        ...pollData,
        pollOptions: pollData.pollOptions.map((item) => {
            const pollOption = {
                ...item,
                persistedId: item.id,
                content:
                    pollData.type === PollTypesEnum.POLL_IMAGES &&
                    item.image?.url !== undefined
                        ? item.image?.url
                        : item.content,
            };

            delete pollOption.image;
            return pollOption;
        }),
    };
}
